import { ButtonHTMLAttributes, useMemo } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import startCase from 'lodash/startCase';

import { Unit } from '@core';
import { useToggle, useUserSettingsWithDefaults, useAnalysisById } from '@hooks';
import { useTranslate } from '@i18n';
import { useActivityReport, useLayoutCustomizations, useUiParameters } from '@queries';
import {
    activeSwingIdAtom,
    activeUserIdAtom,
    activityAtom,
    activityUnitAtom,
    corridorsAtom,
    selectedSwingGuideParameterAtom,
    swingGuideParametersAtom,
    setGoalCorridorParameterAtom,
    layoutCustomCorridorsMapAtom,
} from '@store';
import { Box, colors, Heading3, LayoutIcon, MenuItem, SwingFoundationParameterCard, u, WarningIcon } from '@ui';

import { CustomModuleEditor } from '../CustomModuleEditor';
import { ModuleBase } from '../ModuleBase';

import * as css from './CustomModule.css';

type CustomModuleProps = {
    nodeId: string;
    isEditing?: boolean;
};

export const CustomModule = ({ nodeId, isEditing }: CustomModuleProps) => {
    const activeUserId = useAtomValue(activeUserIdAtom);
    const activity = useAtomValue(activityAtom);
    const activityUnit = useAtomValue(activityUnitAtom);
    const activeSwingId = useAtomValue(activeSwingIdAtom) ?? '';
    const { data: userSettings } = useUserSettingsWithDefaults(activeUserId);
    const { data: customizations } = useLayoutCustomizations(activity?.layoutId || -1);
    const setGoalCorridorParameter = useSetAtom(setGoalCorridorParameterAtom);
    const setSelectedGuideParameterId = useSetAtom(selectedSwingGuideParameterAtom);
    const swingGuideParameters = useAtomValue(swingGuideParametersAtom);
    const parameterIds = useMemo(
        () =>
            customizations
                ?.filter((customization) => customization.parentUiNodeId === nodeId)
                .map(({ childUiNodeId }) => childUiNodeId),
        [customizations, nodeId],
    );
    const { data: parameters } = useUiParameters();
    const parameterNodes = useMemo(
        () => parameters?.filter((parameter) => parameterIds?.includes(parameter.id)),
        [parameterIds, parameters],
    );

    const activeSwingAnalysis = useAnalysisById(activeSwingId);
    const comparisonSwingAnalysis = useAnalysisById(userSettings.comparisonSwingId);
    const { data: activityReport } = useActivityReport(activity?.id);

    const corridors = useAtomValue(corridorsAtom);
    const goalCorridorMap = useAtomValue(layoutCustomCorridorsMapAtom);

    const t = useTranslate();
    const subtitle = t('module_type.custom');

    const title = useMemo(() => startCase(nodeId.split('.')?.[1]) ?? 'Custom Module', [nodeId]);

    const [isSelectingParameters, toggleIsSelectingParameters] = useToggle();

    return (
        <>
            <ModuleBase
                title={title}
                subtitle={subtitle}
                isClosed={isEditing}
                button={
                    <SettingsButton
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleIsSelectingParameters();
                        }}
                    />
                }
            >
                {!parameterNodes?.length && (
                    <Box display="flex" justifyContent="center" width="100%">
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <WarningIcon size={u(4)} color={colors.bluegray[200]} />
                            <Heading3 paddingInlineStart={u(2)}>
                                {'Please select parameters from the settings button above'}
                            </Heading3>
                        </Box>
                    </Box>
                )}
                <Box className={css.kioskModuleLayout}>
                    {parameterNodes?.map((param) => {
                        const { i18n, parameter } = param;
                        const paramValue = activeSwingAnalysis?.data.analysis?.parameter_values?.[parameter.name];
                        const comparisonParamValue =
                            comparisonSwingAnalysis?.data.analysis?.parameter_values?.[parameter.name];
                        const reportValue = activityReport?.[parameter.name];
                        const paramCorridors = corridors?.[parameter.name];
                        const goalCorridors = goalCorridorMap?.[parameter.smpId];

                        return (
                            <SwingFoundationParameterCard
                                key={parameter.smpId}
                                title={t(i18n ?? '-')}
                                unit={parameter.unit as Unit}
                                value={paramValue?.value}
                                mean={reportValue?.mean}
                                stdDev={reportValue?.stdDev}
                                userPreferredUnit={activityUnit}
                                corridorParameterMargins={userSettings.showCorridors ? paramCorridors : null}
                                goalCorridor={userSettings.showGoal ? goalCorridors : null}
                                comparisonValue={userSettings.showComparison ? comparisonParamValue?.value : null}
                                menuItems={
                                    <>
                                        <MenuItem onClick={() => setGoalCorridorParameter(param)}>
                                            Add/adjust goal corridors
                                        </MenuItem>
                                        {swingGuideParameters.has(parameter.smpId) && (
                                            <MenuItem onClick={() => setSelectedGuideParameterId(parameter.smpId)}>
                                                Guide
                                            </MenuItem>
                                        )}
                                    </>
                                }
                            />
                        );
                    })}
                </Box>
            </ModuleBase>
            {isSelectingParameters && activity?.layoutId && (
                <CustomModuleEditor
                    layoutId={activity.layoutId}
                    activityId={activity.id}
                    activeSwingId={activeSwingId ?? undefined}
                    customModuleId={nodeId}
                    onClose={toggleIsSelectingParameters}
                />
            )}
        </>
    );
};

const SettingsButton = ({ onClick }: Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>) => {
    return (
        <button onClick={onClick} className={css.settingsButton}>
            <LayoutIcon weight="fill" color={colors.blue[500]} size={u(2)} />
            settings
        </button>
    );
};
